import React, { useState } from 'react';
import { DimensionValue, Platform, StyleSheet, View } from 'react-native';

import Colors from '../theming/Colors';
import { colorForScheme } from '../theming/Theming';
import KitInput from './KitInput';
import KitTouchableIcon from './KitTouchableIcon';

interface KitCounterInputProps {
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  width?: DimensionValue;
}

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 999999;

export const KitCounterInput: React.FC<KitCounterInputProps> = ({
  value,
  onChange,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  disabled = false,
  width,
}) => {
  const [internalValue, setInternalValue] = useState<number>(
    typeof value === 'number' ? value : 0
  );

  const maxLength = String(max).length;
  const displayValue = typeof value === 'number' ? value : internalValue;
  const displayLength = displayValue.toString().length;
  const clamp = (val: number) => {
    const clampedValue = Math.max(min, Math.min(max, val));

    return clampedValue;
  };

  const updateValue = (newValue: number) => {
    const clampedValue = clamp(newValue);

    if (onChange) onChange(clampedValue);
    else setInternalValue(clampedValue);
  };

  const handleInputChange = (text: string) => {
    let val = text.replace(/\D/g, '');

    if (val.length > maxLength) {
      val = val.slice(0, maxLength);
    }

    const num = val === '' ? 0 : parseInt(val, 10);
    updateValue(num);
  };

  const renderButton = (type: 'plus' | 'minus') => {
    const isPlus = type === 'plus';
    const isDisabled = isPlus
      ? displayValue >= max || disabled
      : displayValue <= min || disabled;

    return (
      <KitTouchableIcon
        name={type}
        onPress={() => {
          updateValue(displayValue + (isPlus ? 1 : -1));
        }}
        disabled={isDisabled}
        size={20}
        buttonSize={44}
        color={isDisabled ? Colors.N500 : Colors.N900}
        underlayColor={Colors.N200}
        hoverScaleValue={1}
        style={styles.button}
        buttonPressScaleValue={isDisabled ? 1 : 0.95}
        enableScaleAnimation={true}
        testID={`kit-touchable-icon-${type}`}
      />
    );
  };

  return (
    <View style={styles.container}>
      {renderButton('minus')}
      <KitInput
        value={String(displayValue)}
        onChangeText={handleInputChange}
        maxLength={maxLength}
        editable={!disabled}
        keyboardType={Platform.OS === 'web' ? 'numeric' : 'number-pad'}
        style={[styles.input, { width: width ?? 100 }]}
        testID='kit-input'
        onBlur={() => {
          if (displayValue === 0 || String(displayValue).trim() === '') {
            updateValue(0);
          }
        }}
        inputStyle={{
          textAlign: 'center',
          fontSize:
            displayLength <= 3
              ? 16
              : displayLength === 4
              ? 14
              : displayLength === 5
              ? 13
              : 12,
          paddingBottom: Platform.OS === 'ios' && displayLength >= 4 ? 5 : 0,
          paddingLeft:
            displayLength < 3
              ? 14
              : displayLength === 4
              ? 10
              : displayLength === 5
              ? 6
              : 4,
          paddingRight:
            displayLength < 3
              ? 14
              : displayLength === 4
              ? 10
              : displayLength === 5
              ? 6
              : 4,
          lineHeight: 22,
          color: displayValue === 0 ? Colors.N500 : Colors.N900,
        }}
      />
      {renderButton('plus')}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  input: {
    borderRadius: 12,
    backgroundColor: Colors.N75,
  },
  button: {
    backgroundColor:
      colorForScheme?.({ dark: Colors.N900, light: Colors.N100 }) ||
      Colors.N100,
  },
});

export default KitCounterInput;
